import React from 'react';
import { X, Calendar, UserCheck, ArrowRight, Sparkles } from 'lucide-react';

const CourseTypeModal = ({ isOpen, onClose, onSelectType, onSelectAI }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4 transition-all">
            <div className="bg-white w-full h-full md:h-auto md:max-w-2xl md:rounded-2xl shadow-2xl overflow-hidden animate-fadeIn flex flex-col">
                {/* Header */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-100 flex items-center justify-between safe-area-top">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-800">Create New Course</h2>
                        <p className="text-xs md:text-sm text-gray-600">Choose the type of journey you want to create</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6 flex-1 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Preboarding Option */}
                        <button
                            onClick={() => onSelectType('preboarding')}
                            className="group p-5 md:p-6 rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50/50 transition-all text-left"
                        >
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-purple-100 flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                                <Calendar className="w-6 h-6 md:w-7 md:h-7 text-purple-600" />
                            </div>
                            <h3 className="text-base md:text-lg font-bold text-gray-800 mb-2">Preboarding Course</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                For new hires <strong>before</strong> their start date. Perfect for paperwork,
                                welcome messages, and preparation tasks.
                            </p>
                            <div className="flex items-center text-purple-600 font-medium text-sm">
                                Select Preboarding
                                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </button>

                        {/* Onboarding Option */}
                        <button
                            onClick={() => onSelectType('onboarding')}
                            className="group p-5 md:p-6 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left"
                        >
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                                <UserCheck className="w-6 h-6 md:w-7 md:h-7 text-blue-600" />
                            </div>
                            <h3 className="text-base md:text-lg font-bold text-gray-800 mb-2">Onboarding Course</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                For employees <strong>after</strong> they start. Ideal for training,
                                policies, team introductions, and role-specific tasks.
                            </p>
                            <div className="flex items-center text-blue-600 font-medium text-sm">
                                Select Onboarding
                                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </button>
                    </div>

                    {/* AI Creation Option */}
                    {onSelectAI && (
                        <div className="mt-4">
                            <div className="text-center text-sm text-gray-400 mb-4">— or —</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <button
                                    onClick={() => onSelectAI('preboarding')}
                                    className="group p-4 rounded-xl border-2 border-dashed border-purple-300 hover:border-purple-500 bg-gradient-to-br from-purple-50 to-violet-50 hover:from-purple-100 hover:to-violet-100 transition-all text-left"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                                            <Sparkles className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-800">AI Preboarding</h4>
                                            <p className="text-xs text-gray-500">Let AI help you build it</p>
                                        </div>
                                    </div>
                                </button>
                                <button
                                    onClick={() => onSelectAI('onboarding')}
                                    className="group p-4 rounded-xl border-2 border-dashed border-blue-300 hover:border-blue-500 bg-gradient-to-br from-blue-50 to-violet-50 hover:from-blue-100 hover:to-violet-100 transition-all text-left"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                                            <Sparkles className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-800">AI Onboarding</h4>
                                            <p className="text-xs text-gray-500">Let AI help you build it</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Info Note */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">
                            <strong>Note:</strong> A course can only be one type. If you need both preboarding
                            and onboarding journeys, create separate courses for each phase.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseTypeModal;

